import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  Loader2,
  CheckCheck,
  Check,
  XCircle,
  Inbox,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useNotifications } from '@/contexts/NotificationsContext';
import SeverityBadge from '@/pages/monitoring/components/SeverityBadge';
import { fetchNotifications } from '@/pages/monitoring/services/monitoring.services';

const PAGE_SIZE = 25;

const ESTADOS = [
  { id: 'pendientes', label: 'Pendientes', value: ['nueva', 'leida'] },
  { id: 'nueva', label: 'Sin leer', value: 'nueva' },
  { id: 'resuelta', label: 'Resueltas', value: 'resuelta' },
  { id: 'descartada', label: 'Descartadas', value: 'descartada' },
  { id: 'todas', label: 'Todas', value: undefined },
];

const TIPOS = [
  { id: '', label: 'Todos los tipos' },
  { id: 'registro_faltante', label: 'Registro faltante' },
  { id: 'metrica_fuera_rango', label: 'Métrica fuera de rango' },
  { id: 'corte_faltante', label: 'Consolidado faltante' },
];

const SEVERIDADES = [
  { id: '', label: 'Toda severidad' },
  { id: 'critica', label: 'Crítica' },
  { id: 'alta', label: 'Alta' },
  { id: 'media', label: 'Media' },
];

const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Bandeja de notificaciones de monitoreo con filtros, lectura y cierre. */
const NotificationsTab = () => {
  const { toast } = useToast();
  const { markRead, resolve, refresh: refreshBadge } = useNotifications();

  const [estadoId, setEstadoId] = useState('pendientes');
  const [tipo, setTipo] = useState('');
  const [severidad, setSeveridad] = useState('');
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [notas, setNotas] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const estado = ESTADOS.find((e) => e.id === estadoId)?.value;
      const resp = await fetchNotifications({
        limit: PAGE_SIZE,
        page,
        estado,
        tipo: tipo || undefined,
        severidad: severidad || undefined,
      });
      if (!resp?.state) throw new Error(resp?.message || 'Respuesta inválida');
      setItems(resp.data || []);
      setTotalRecords(resp.totalRecords || 0);
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudieron cargar las notificaciones.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [estadoId, tipo, severidad, page, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Cambiar de filtro debe volver a la primera página.
  useEffect(() => {
    setPage(1);
  }, [estadoId, tipo, severidad]);

  const handleMarkAllRead = async () => {
    try {
      const resp = await markRead({ all: true });
      toast({ title: 'Listo', description: resp?.message || 'Notificaciones marcadas.' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleClose = async (item, estado) => {
    setBusyId(item._id);
    try {
      await resolve(item._id, { estado, nota: notas[item._id] || undefined });
      toast({
        title: estado === 'resuelta' ? 'Notificación resuelta' : 'Notificación descartada',
        description: item.titulo,
      });
      setNotas((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <div className="flex flex-wrap gap-2">
                {ESTADOS.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setEstadoId(e.id)}
                    className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                      estadoId === e.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:border-primary/40'
                    }`}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Tipo</Label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/20"
              >
                {TIPOS.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">Severidad</Label>
              <select
                value={severidad}
                onChange={(e) => setSeveridad(e.target.value)}
                className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/20"
              >
                {SEVERIDADES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <Badge variant="outline" className="text-muted-foreground">
                {totalRecords} resultado(s)
              </Badge>
              <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
                <CheckCheck className="mr-2 h-4 w-4" />
                Marcar todas leídas
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  load();
                  refreshBadge();
                }}
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      {loading && items.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-3 text-sm text-muted-foreground">Cargando notificaciones…</span>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Inbox className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No hay notificaciones con estos filtros.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => {
            const abierta = item.estado === 'nueva' || item.estado === 'leida';
            return (
              <motion.div
                key={item._id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.02, 0.3) }}
              >
                <Card
                  className={
                    item.estado === 'nueva' ? 'border-l-4 border-l-primary' : undefined
                  }
                >
                  <CardContent className="pt-4 pb-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <SeverityBadge severidad={item.severidad} />
                          <span className="text-sm font-semibold text-foreground">
                            {item.titulo}
                          </span>
                          {!abierta && (
                            <Badge variant="secondary" className="text-xs">
                              {item.estado}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{item.mensaje}</p>
                        <p className="text-xs text-muted-foreground/70 mt-1.5">
                          {item.lavanderia?.nombre} · {item.periodo?.label} ·{' '}
                          {fmtFecha(item.createdAt)}
                        </p>
                        {item.notaResolucion && (
                          <p className="text-xs text-muted-foreground mt-1.5 italic">
                            Nota: {item.notaResolucion}
                          </p>
                        )}
                      </div>

                      {abierta && (
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            placeholder="Nota (opcional)"
                            className="h-9 w-44"
                            value={notas[item._id] || ''}
                            onChange={(e) =>
                              setNotas((p) => ({ ...p, [item._id]: e.target.value }))
                            }
                          />
                          <Button
                            size="sm"
                            disabled={busyId === item._id}
                            onClick={() => handleClose(item, 'resuelta')}
                          >
                            {busyId === item._id ? (
                              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="mr-2 h-3.5 w-3.5" />
                            )}
                            Resolver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busyId === item._id}
                            onClick={() => handleClose(item, 'descartada')}
                          >
                            <XCircle className="mr-2 h-3.5 w-3.5" />
                            Descartar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground tabular-nums">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
